/**
 * Dates and plain numbers in the user's locale (#2724).
 *
 * Seventeen places called `toLocaleDateString()` / `toLocaleString()` with no
 * locale argument, so the browser decided. On an en-US browser a Finnish
 * workshop saw `9/29/2026` instead of `29.9.2026` — and the ambiguity is
 * real, not theoretical: `9/10/2026` is 9 October to a Finn and 10 September
 * to the browser. Mileage came out `12,345 km` where Finnish groups with a
 * space.
 *
 * These helpers take the locale from i18next rather than hardcoding `fi-FI`,
 * so an English user of a Finnish workshop gets their own conventions. That
 * is why they read `i18n.language` at call time instead of accepting a
 * locale argument every caller would have to remember to pass.
 *
 * Dates that are part of a *layout* (calendar headers, chart axes) keep using
 * date-fns with its explicit format strings — this is for the incidental
 * "when did this happen" text scattered through the UI. Those date-fns calls
 * take their locale from `dateFnsLocale()` below (#3782).
 */

import type { Locale } from 'date-fns';
import { enUS, fi, sv } from 'date-fns/locale';
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n/languages';

/**
 * Current UI language, falling back to Finnish.
 *
 * Read from localStorage rather than from i18next. Importing '@/lib/i18n'
 * runs the whole bootstrap on import, and importing `getI18n` from
 * react-i18next makes every test that mocks that module fail to load — both
 * were tried, and both broke seven test files.
 *
 * The key is the one i18next's own language detector persists to
 * (`lookupLocalStorage` in lib/i18n), so a language change is picked up on
 * the next render without this module depending on i18next at all.
 */
const LANGUAGE_STORAGE_KEY = 'staff-portal-lang';

/**
 * The UI language as one of the supported codes. A stored region variant
 * (`sv-FI`) resolves to its language; anything unknown falls back to Finnish.
 */
export function currentLanguage(): SupportedLanguage {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // Private windows and blocked site data throw on access.
  }
  const code = stored?.toLowerCase().split('-')[0];
  return supportedLanguages.find((lng) => lng === code) ?? 'fi';
}

/**
 * Intl locale per UI language (#3782). Swedish is `sv-FI`, not `sv`: the
 * users are Finland-Swedish, and bare `sv` prints Sweden's ISO dates
 * (`2026-09-29`) where Finland writes `29.9.2026`.
 */
const INTL_LOCALES: Record<SupportedLanguage, string> = {
  fi: 'fi-FI',
  sv: 'sv-FI',
  en: 'en',
};

const DATE_FNS_LOCALES: Record<SupportedLanguage, Locale> = {
  fi,
  sv,
  en: enUS,
};

/** Intl locale for `toLocaleString` calls that need their own options. */
export function intlLocale(): string {
  return INTL_LOCALES[currentLanguage()];
}

/**
 * date-fns locale for the UI language — month and weekday names in calendar
 * headers, `formatDistance` and the like. Read at call time like the rest of
 * this module; components re-render on a language change through their own
 * `useTranslation`.
 */
export function dateFnsLocale(): Locale {
  return DATE_FNS_LOCALES[currentLanguage()];
}

/** Anything a date can arrive as from the API or a parser. */
type DateLike = string | number | Date | null | undefined;

function toDate(value: DateLike): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Date only — `29.9.2026` in Finnish.
 *
 * Returns an em dash for a missing or unparseable value rather than
 * `Invalid Date`, which is what the raw calls printed.
 */
export function formatDate(value: DateLike): string {
  const date = toDate(value);
  return date ? date.toLocaleDateString(intlLocale()) : '—';
}

/** Date and time — `29.9.2026 klo 14.05` in Finnish. */
export function formatDateTime(value: DateLike): string {
  const date = toDate(value);
  return date ? date.toLocaleString(intlLocale()) : '—';
}

/**
 * A plain count — mileage, interaction totals.
 *
 * Finnish groups thousands with a space (`12 345`), English with a comma.
 * Not for money: that is `formatCentsForDisplay` in `utils/money.ts`, which
 * also carries the € and the integer-cents contract.
 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString(intlLocale());
}
